import { AlertCircle, ClipboardList, Plus, ShieldAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  type IncidentDto,
  type IncidentSeverity,
  type IncidentStatus,
  useCreateIncident,
  useIncidentsList,
} from '../api/useIncidents.js';
import { useRoster } from '../api/useRoster.js';
import { EmptyState } from '../components/EmptyState.js';
import { cn } from '../lib/cn.js';
import { formatRelative } from '../lib/format.js';

// Incidents list — first-class records for WHS / NDIS reporting. An incident can
// originate from an alarm (the common case, via the "Create incident" button on the
// alarm detail page) or be opened retrospectively. The old "resolved alarms" view that
// used to live here is now reachable via the Alarms page with a status filter.

type Tab = 'active' | 'closed' | 'all';

const tabs: { key: Tab; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
];

export default function Incidents() {
  const [searchParams] = useSearchParams();
  const prefillAlarmId = searchParams.get('alarm_id');
  const prefillAffectedId = searchParams.get('affected_user_id');

  const [tab, setTab] = useState<Tab>('active');
  const [showCreate, setShowCreate] = useState(Boolean(prefillAlarmId));

  // "Active" bundles both open and under_review — two queries so the status filter on
  // the server stays straightforward. For small lists the duplicate fetch is negligible.
  const openList = useIncidentsList(
    tab === 'active' ? { status: 'open' } : tab === 'closed' ? { status: 'closed' } : {},
  );
  const underReviewList = useIncidentsList(tab === 'active' ? { status: 'under_review' } : {});
  const combined = useMemo(() => {
    // Newest-first across every tab — a fresh incident should never be buried
    // below older ones. Active bundles open + under_review.
    const rows =
      tab !== 'active'
        ? [...(openList.data?.incidents ?? [])]
        : [...(openList.data?.incidents ?? []), ...(underReviewList.data?.incidents ?? [])];
    return rows.sort((x, y) => new Date(y.created_at).getTime() - new Date(x.created_at).getTime());
  }, [tab, openList.data, underReviewList.data]);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <span className="eyebrow">Compliance</span>
          <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
            Incidents
          </h1>
          <p className="text-sm text-ink-faint">
            Formalised post-event records with severity, status, and timeline. Used for WHS and NDIS
            reporting.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex gap-0.5 rounded-md border border-line bg-surface-1 p-1 shadow-xs">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  'rounded-sm px-3.5 py-1.5 text-xs font-semibold transition',
                  tab === t.key ? 'bg-brand-purple text-white' : 'text-ink-faint hover:text-ink',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white shadow-glow-primary hover:bg-brand-purple/90"
          >
            <Plus className="h-4 w-4" />
            New incident
          </button>
        </div>
      </div>

      {openList.isError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Failed to load incidents: {(openList.error as Error).message}
        </div>
      )}

      {!openList.isLoading && combined.length === 0 && (
        <EmptyState
          icon={ClipboardList}
          title={tab === 'closed' ? 'No closed incidents' : 'No incidents yet'}
          description={
            tab === 'closed'
              ? 'Closed incidents will show here with their full timeline preserved.'
              : 'Open a new incident after a duress event, a near miss, or when something warrants a formal record.'
          }
        />
      )}

      {combined.length > 0 && (
        <div className="panel">
          <table className="min-w-full divide-y divide-line">
            <thead className="bg-surface-3">
              <tr>
                <Th>Title</Th>
                <Th>Severity</Th>
                <Th>Status</Th>
                <Th>Affected worker</Th>
                <Th>Opened</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line bg-white">
              {combined.map((i) => (
                <Row key={i.id} incident={i} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <NewIncidentModal
          initialAlarmId={prefillAlarmId}
          initialAffectedUserId={prefillAffectedId}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
}

function Row({ incident }: { incident: IncidentDto }) {
  return (
    <tr className="hover:bg-surface-3">
      <Td>
        <Link to={`/incidents/${incident.id}`} className="block">
          <div className="text-sm font-medium text-ink">{incident.title}</div>
          <div className="mt-0.5 line-clamp-1 text-xs text-ink-faint">{incident.description}</div>
          {incident.alarm_id && (
            <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-red-600">
              <ShieldAlert className="h-3 w-3" />
              linked to alarm
            </div>
          )}
        </Link>
      </Td>
      <Td>
        <SeverityChip severity={incident.severity} />
      </Td>
      <Td>
        <StatusChip status={incident.status} />
      </Td>
      <Td className="text-sm text-ink-dim">
        {incident.affected_user_display_name ?? <span className="text-ink-mute">—</span>}
      </Td>
      <Td className="text-xs text-ink-faint">
        <div>{formatRelative(incident.created_at)}</div>
        <div className="text-[10px] text-ink-mute">
          {incident.created_by_display_name ?? 'system'}
        </div>
      </Td>
      <Td className="text-right">
        <Link
          to={`/incidents/${incident.id}`}
          className="rounded-md border border-line bg-white px-3 py-1 text-xs font-medium text-ink-dim transition hover:bg-surface-3"
        >
          View
        </Link>
      </Td>
    </tr>
  );
}

const SEVERITY_CHIP: Record<IncidentSeverity, string> = {
  low: 'bg-surface-3 text-ink-dim ring-gray-200',
  medium: 'bg-amber-50 text-amber-700 ring-amber-200',
  high: 'bg-orange-100 text-orange-800 ring-orange-300',
  // Solid red so a critical incident is unmissable against a soft-orange high.
  critical: 'bg-red-600 text-white ring-red-700',
};

const STATUS_CHIP: Record<IncidentStatus, string> = {
  open: 'bg-blue-50 text-blue-700 ring-blue-200',
  under_review: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  closed: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  cancelled: 'bg-surface-3 text-ink-faint ring-gray-200',
};

export function SeverityChip({ severity }: { severity: IncidentSeverity }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ring-1 ring-inset',
        SEVERITY_CHIP[severity],
      )}
    >
      {severity}
    </span>
  );
}

export function StatusChip({ status }: { status: IncidentStatus }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        STATUS_CHIP[status],
      )}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-faint',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 text-sm', className)}>{children}</td>;
}

function NewIncidentModal({
  initialAlarmId,
  initialAffectedUserId,
  onClose,
}: {
  initialAlarmId: string | null;
  initialAffectedUserId: string | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const create = useCreateIncident();
  const roster = useRoster();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<IncidentSeverity>('medium');
  const [affectedId, setAffectedId] = useState<string | null>(initialAffectedUserId);
  const [tagInput, setTagInput] = useState('');

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const tags = tagInput
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const res = await create.mutateAsync({
      title: title.trim(),
      description: description.trim(),
      severity,
      alarm_id: initialAlarmId,
      affected_user_id: affectedId ?? null,
      tags: tags.length > 0 ? tags : undefined,
    });
    navigate(`/incidents/${res.incident.id}`);
    onClose();
  }

  const workers = roster.data?.members ?? [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-incident-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        className="w-full max-w-xl rounded-lg bg-white p-5 shadow-2xl"
      >
        <h2 id="new-incident-title" className="mb-1 text-base font-semibold text-ink">
          Open incident
        </h2>
        <p className="mb-4 text-xs text-ink-faint">
          {initialAlarmId
            ? 'Linked to the alarm you came from — follow-ups and timeline live on this new record.'
            : 'Use for retrospective reports, near-misses, or post-alarm documentation.'}
        </p>

        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">Title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Duress at 4 Pitt St — worker confirmed safe"
              required
              maxLength={200}
              className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              maxLength={10_000}
              rows={5}
              placeholder="What happened, who was involved, outcome…"
              className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="mb-1 text-xs font-medium text-ink-dim">Severity</div>
              <div className="flex flex-wrap gap-1">
                {(['low', 'medium', 'high', 'critical'] as IncidentSeverity[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSeverity(s)}
                    className={cn(
                      'rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize',
                      severity === s
                        ? 'border-brand-purple bg-brand-purple text-white'
                        : 'border-line bg-white text-ink-dim hover:border-line-strong',
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-dim">Affected worker</span>
              <select
                value={affectedId ?? ''}
                onChange={(e) => setAffectedId(e.target.value || null)}
                className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
              >
                <option value="">—</option>
                {workers.map((w) => (
                  <option key={w.user_id} value={w.user_id}>
                    {w.display_name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">Tags (comma-separated)</span>
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="lone_worker, verbal_abuse, after_hours"
              className="rounded-md border border-line-strong px-3 py-1.5 text-xs focus:border-brand-purple focus:outline-none"
            />
          </label>

          {create.isError && (
            <div className="inline-flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-800">
              <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
              {(create.error as Error).message}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-line pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink-dim hover:bg-surface-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!title.trim() || !description.trim() || create.isPending}
              className="rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
            >
              {create.isPending ? 'Opening…' : 'Open incident'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
